/**
 * Format a byte count into a human-readable file size string.
 */
export function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

/**
 * Get initials from a full name string (e.g. "John Doe" → "JD").
 * Handles comma-separated names, single names, and null/undefined.
 */
export function getInitials(name: string | null | undefined): string {
  if (!name) return '?';
  const parts = name.split(/[\s,]+/).filter(Boolean);
  if (parts.length >= 2) {
    return (parts[0][0] + parts[1][0]).toUpperCase();
  }
  return (parts[0]?.[0] ?? '?').toUpperCase();
}

/**
 * Get initials from separate first/last name fields.
 */
export function getInitialsFromParts(
  firstName: string | null | undefined,
  lastName: string | null | undefined,
): string {
  const f = firstName?.[0] ?? '';
  const l = lastName?.[0] ?? '';
  return (f + l).toUpperCase() || '?';
}

/**
 * Whether a transcript has usable content to generate a document from.
 *
 * Mirrors the backend `Encounter.has_transcript`: empty or whitespace-only
 * counts as "no transcript". Used to gate the document-generation controls.
 */
export function hasUsableTranscript(transcript: string | null | undefined): boolean {
  return Boolean(transcript && transcript.trim());
}

/**
 * Resolve the title shown for a generated document in the Inbox / lists.
 *
 * Order of preference:
 *   1. `additional_context.title` (user-entered Quick Start title)
 *   2. `template?.name` (the template the doc was generated from)
 *   3. `'Generated report'` (defensive fallback)
 *
 * Whitespace-only titles are treated as absent so legacy rows with a blank
 * string don't leak through.
 */
export function resolveGeneratedDocumentTitle(args: {
  additional_context?: Record<string, unknown> | null;
  template?: { name?: string | null } | null;
}): string {
  const rawTitle = args.additional_context?.title;
  const userTitle = typeof rawTitle === 'string' ? rawTitle.trim() : '';
  if (userTitle) return userTitle;
  const templateName = args.template?.name?.trim();
  return templateName || 'Generated report';
}

/** Standard EMR marker for "No Known Drug Allergies". */
export const NKDA_LABEL = 'NKDA';

/**
 * Normalize a patient's allergy list for display. An empty (or whitespace-only)
 * list is the clinically explicit "NKDA" state — callers render NKDA_LABEL, not
 * a blank. The stored value is always a string[]; "NKDA" is never stored.
 */
export function getAllergyDisplay(allergies: string[] | null | undefined): {
  isNkda: boolean;
  items: string[];
} {
  const items = (allergies ?? []).map((a) => a.trim()).filter(Boolean);
  return { items, isNkda: items.length === 0 };
}
